<template>
  <div class="py-4">
    <v-container fluid>
      <!-- Stats -->
      <v-row>
        <v-col cols="12" md="" v-for="stat in statsCards" :key="stat.label">
          <v-card>
            <v-card-text>
              <div class="text-h4">{{ stat.value }}</div>
              <div class="text-subtitle-1">{{ stat.label }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filter Tabs -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-tabs v-model="activeTab">
            <v-tab v-for="tab in tabs" :key="tab.key">{{ tab.label }}</v-tab>
          </v-tabs>
        </v-col>
      </v-row>

      <!-- Search and Filters -->
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            label="Search..."
            prepend-icon="mdi-magnify"
            outlined
            dense
          ></v-text-field>
        </v-col>
        
        <v-col cols="12" md="3">
          <v-select
            v-model="selected_group_main"
            :items="groups"
            item-text="name"
            item-value="id"
            label="Kundengruppe"
            clearable
            dense
            outlined
            multiple
            chips
            small-chips
            deletable-chips
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selected_group_type"
            :items="groupTypes"
            item-text="name"
            item-value="name"
            label="Untergruppe"
            clearable
            dense
            outlined
            multiple
            chips
            small-chips
            deletable-chips
          ></v-select>
        </v-col>
        
        <v-col cols="12" md="3" class="text-right">
          <v-btn color="primary" @click="openNewCustomerDialog" small class="mr-1">
            <v-icon left small>mdi-plus</v-icon>
            Neuer Kunde
          </v-btn>


          <v-btn color="success" @click="exportData" small>
            <v-icon left small>mdi-export</v-icon>
            Export
          </v-btn>
        </v-col>
      </v-row>
      <!-- Customer/Lead List -->
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="filteredCustomers"
            :search="search"
            :items-per-page="25"
            class="elevation-1"
            @click:row="openCustomerDetail"
          >

            <template v-slot:item.isInterest="{ item }">
              <v-chip 
                small 
                :color="getConservationStatusColor(item.isInterest)"
                dark
              >
                {{ getConservationStatusLabel(item.isInterest) }}
              </v-chip>
            </template>
            <template v-slot:item.isCustomer="{ item }">
              <v-chip small :color="item.isCustomer === '1' ? 'success' : 'warning'">
                {{ item.isCustomer === '1' ? 'Kunde' : 'Potenziell' }}
              </v-chip>
            </template>


            <template v-slot:item.customer_before="{ item }">
              <v-chip small :color="getCustomerBeforeColor(item.customer_before)">
                {{ getCustomerBeforeLabel(item.customer_before) }}
              </v-chip>
            </template>

            <template v-slot:item.lastConversation="{ item }">
              <span v-if="item.lastConversation">{{ formatDate(item.lastConversation) }}</span>
              <span v-else class="text--disabled">None</span>
            </template>

            <template v-slot:item.callback_date="{ item }">
              <span v-if="item.callback_date">{{ formatDate(item.callback_date) }}</span>
              <span v-else class="text--disabled">None</span>
            </template>
            <template v-slot:item.callback_time="{ item }">
              <span v-if="item.callback_time">{{ formatTime(item.callback_time) }}</span>
              <span v-else class="text--disabled">None</span>
            </template>
            
            <template v-slot:item.note="{item}">
              <p class="tooltip">{{ item.note.toString().substring(0, 15) }}
                <span class="tooltiptext">{{ item.note.toString() }}</span>
              </p>
            </template>

            <template v-slot:item.rating="{item}">
              <v-row>
                <div style="display: inline-block; white-space: nowrap;">
                  <div style="display: inline-block;">
                    <template v-if="item.rating && item.rating !== '???'">
                      <v-icon v-if="Math.round(Number(item.rating)) == 1" color="red" style="padding: 3px;">mdi-star</v-icon>
                      <v-icon v-else-if="Math.round(Number(item.rating)) == 2" color="orange" style="padding: 3px;">mdi-star</v-icon>
                      <v-icon v-else-if="Math.round(Number(item.rating)) == 3" color="yellow" style="padding: 3px;">mdi-star</v-icon>
                      <v-icon v-else-if="Math.round(Number(item.rating)) == 4" color="lime" style="padding: 3px;">mdi-star</v-icon>
                      <v-icon v-else-if="Math.round(Number(item.rating)) == 5" color="green" style="padding: 3px;">mdi-star</v-icon>
                    </template>
                    <v-icon v-else color="grey" style="padding: 3px;">mdi-star-outline</v-icon>
                  </div>
                  <div style="display: inline-block;" v-if="item.rating && item.rating !== '???' && item.rating_in_percent">
                    {{ item.rating_in_percent }}%
                  </div>
                  <div style="display: inline-block;" v-else>--%</div>
                </div>
              </v-row>
            </template>
            <template v-slot:item.worker="{ item }">
             <span small v-if="item.worker" color="primary" dark>
              {{ item.worker }}
             </span>
             <span v-else class="text--disabled">None</span>
            </template>
            
            <template v-slot:item.actions="{ item }">
              <v-btn icon @click.stop="openConversationDialog(item)">
                <v-icon>mdi-message-plus</v-icon>
              </v-btn>
              <v-btn icon @click.stop="openCustomerDetail(item)">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Customer Detail Dialog Component -->
      <CustomerDetailDialog
        v-model="detailDialog"
        :customer="selectedCustomer"
        :conversations="conversations"
        :contact-persons="contactPersons"
        :conservation-statuses="conservationStatuses"
        @update-customer="handleUpdateCustomer" 
        @fieldUpdate="handleFieldUpdate"
        @open-conversation="openConversationDialog"
        @open-email="openEmailDialog"
        @open-contact-person="openContactPersonDialog"
        @edit-note="handleEditNote"
        @refresh="handleRefresh"
        @delete-conversation="handleDeleteConversation"
        @save="handleConversationSave"
       
      />

      <!-- Conversation Dialog Component -->
      <ConversationDialog
        v-model="conversationDialog"
        :customer="selectedCustomer"
        :contactPersons="contactPersons"
        :conservationStatuses="conservationStatuses"
        @save="handleConversationSave"
        @save-contact-person="saveContactPerson"
      />

      <!-- Email Dialog Component -->
      <EmailDialog
        v-model="emailDialog"
        :customer="selectedCustomer"
        :contact-persons="contactPersons"
        :contact="selectedContact"
        @send="sendEmail"
      />

      <!-- Contact Person Dialog Component -->
      <ContactPersonDialog
        v-model="contactPersonDialog"
        :contact="editingContactPerson"
        :customer="selectedCustomer"
        @save="saveContactPerson"
        @delete="deleteContactPerson"
      />
    </v-container>
  </div>
</template>

<script>
import CustomerDetailDialog from  '@/components/CustomerDetailDialog.vue'
import ConversationDialog from '@/components/ConversationDialog.vue'
import EmailDialog from '@/components/EmailDialog.vue'
import ContactPersonDialog from '@/components/ContactPersonDialog.vue'


export default {
  name: 'CRMDashboard',
  
  components: {
    CustomerDetailDialog,
    ConversationDialog,
    EmailDialog,
    ContactPersonDialog,
  
  },

  data() {
    return {
      activeTab: 0,
      search: '',
      customers: [],
      conversations: [],
      contactPersons: [],
      recruitments : [],
      selectedCustomer: null,
      detailDialog: false,
      conversationDialog: false,
      emailDialog: false,
      contactPersonDialog: false,
   
      editingContactPerson: null,
      selected_group_main: [],
      selected_group_type: [],
      groupTypes: [],
      groups: [],
      selectedContact: null, 
      exportLoading: false,
      conservationStatuses: [],
      
      stats: {
        totalCustomers: 0,
        totalLeads: 0,
        interestedLeads: 0,
        conversationsThisMonth: 0,
        inactiveCustomers: 0,
        offersThisMonth: 0
      },

      interestOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
        { text: 'Unknown', value: 'unknown' },
        { text: 'Later', value: 'later' }
      ]
    }
  },

  computed: {
    statsCards() {
      return [
        { label: 'Bestandskunden', value: this.stats.totalCustomers },
        { label: 'Potentielle Kunden', value: this.stats.totalLeads },
        { label: 'Interessiert', value: this.stats.interestedLeads },
       
        { label: 'Inaktiv (6+ Monate)', value: this.stats.inactiveCustomers },
        { label: 'Angebote (Monat)', value: this.stats.offersThisMonth }, 
        { label: 'Aktivitäten (Monat)', value: this.stats.conversationsThisMonth }
        
      ]
    },

    headers() {
      let headers = [];
      
      switch (this.activeTab) {
        case 0: // All 
          headers = [
            { text: 'Status', value: 'isCustomer', width: '100px' },
            { text: 'Firma', value: 'company', sortable: true },
            { text: 'PLZ', value: 'zipcode', width: '80px' },
            { text: 'Stadt', value: 'city' },
            { text: 'Kundengruppe', value: 'group_main_text' },
            { text: 'Untergruppe', value: 'group_type_text' },
            { text: 'Telefon', value: 'phone' },
            { text: 'Letztes Gespräch', value: 'lastConversation' },
            { text: 'Gesprächstatus', value: 'isInterest', width: '130px' }
          ];
          break;
          
        case 1: // Customers 
          headers = [
            { text: 'Firma', value: 'company', sortable: true },
            { text: 'PLZ', value: 'zipcode', width: '80px' },
            { text: 'Stadt', value: 'city' },
            { text: 'Telefon', value: 'phone' },
            { text: 'Kundengruppe', value: 'group_main_text' },
            { text: 'Untergruppe', value: 'group_type_text' },
            { text: 'Bewertung', value: 'rating', width: '100px' },
            { text: 'Letzte Abholung', value: 'last_pickup' },
          //  { text: 'Abholungen', value: 'pickup_qty', width: '100px' },// to check
            { text: 'Letztes Gespräch', value: 'lastConversation' },
            { text: 'Gesprächstatus', value: 'isInterest', width: '130px' },
            { text: 'Bemerkung', value: 'note', width: '150px' }
          ];
          break;

        case 2: // Leads
          headers = [
            { text: 'Firma', value: 'company',sortable: true },
            { text: 'PLZ', value: 'zipcode' },
            { text: 'Stadt', value: 'city' },
            { text: 'Telefon', value: 'phone' },
            { text: 'Kundengruppe', value: 'group_main_text' },
            { text: 'Ehemaliger Kunde', value: 'customer_before' },// i need to add this column ( yes/no/unknown)
            { text: 'Mitarbeiter', value: 'worker'},
            { text: 'Letztes Gespräch', value: 'lastConversation' },
            { text: 'Wiedervorlage', value: 'callback_date', width: '150px' },
            { text: 'Interesse', value: 'isInterest' },
            { text: 'Bemerkung', value: 'note', width: '150px' }
          ];
          break;
          
        case 3: // Interested 
          headers = [
            { text: 'Firma', value: 'company', sortable: true },
            { text: 'PLZ', value: 'zipcode' },
            { text: 'Stadt', value: 'city' },
            { text: 'Telefon', value: 'phone' },
            { text: 'Kundengruppe', value: 'group_main_text' },
            { text: 'Ehemaliger Kunde', value: 'customer_before' },// i need to add this column ( yes/no/unknown)
            { text: 'Mitarbeiter', value: 'worker'},
            { text: 'Letztes Gespräch', value: 'lastConversation' },
            { text: 'Wiedervorlage', value: 'callback_date'},
            { text: 'Interesse', value: 'isInterest' },
            { text: 'Bemerkung', value: 'note'}
          ];
          break;

        case 4: // Not Interested
          headers = [
            { text: 'Firma', value: 'company', sortable: true },
            { text: 'PLZ', value: 'zipcode' },
            { text: 'Stadt', value: 'city' },
            { text: 'Telefon', value: 'phone' },
            { text: 'Kundengruppe', value: 'group_main_text' },
            { text: 'Ehemaliger Kunde', value: 'customer_before' },// i need to add this column ( yes/no/unknown)
            { text: 'Mitarbeiter', value: 'worker'},
            { text: 'Letztes Gespräch', value: 'lastConversation' },
            { text: 'Wiedervorlage', value: 'callback_date'},
            { text: 'Interesse', value: 'isInterest' },
            { text: 'Bemerkung', value: 'note'}
          ];
          break;

        case 5: // Later
          headers = [
            { text: 'Datum', value: 'callback_date', width: '150px' },
            { text: 'Uhrzeit', value: 'callback_time'},
            { text: 'Mitarbeiter', value: 'worker', width: '120px' },
            { text: 'Firma', value: 'company', sortable: true },
            { text: 'PLZ', value: 'zipcode', width: '80px' },
            { text: 'Stadt', value: 'city' },
            { text: 'Telefon', value: 'phone' },
            { text: 'Kundengruppe', value: 'group_main_text' },
            { text: 'Letztes Gespräch', value: 'lastConversation' },
            { text: 'Bemerkung', value: 'note', width: '150px' }
          ];
          break;

        case 6: // Inactive
          headers = [
            { text: 'Firma', value: 'company', sortable: true },
            { text: 'PLZ', value: 'zipcode', width: '80px' },
            { text: 'Stadt', value: 'city' },
            { text: 'Telefon', value: 'phone' },
            { text: 'Kundengruppe', value: 'group_main_text' },
            { text: 'Untergruppe', value: 'group_type_text' },
            { text: 'Bewertung', value: 'rating', width: '100px' },
            { text: 'Letzte Abholung', value: 'last_pickup' },
       //     { text: 'Abholungen', value: 'pickup_qty', width: '100px' },
            { text: 'Letztes Gespräch', value: 'lastConversation' },
            { text: 'Gesprächstatus', value: 'isInterest', width: '130px' },
            { text: 'Bemerkung', value: 'note', width: '150px' }
          ];
          break;
      }
      
      // Add Actions column to all tabs
      headers.push({ text: 'Actions', value: 'actions', sortable: false, width: '150px' });
      
      return headers;
    },

    tabs() {
        const overdueCount = this.overdueRemindersCount
        const callbackTabName = overdueCount > 0 ? `Wiedervorlage (${overdueCount})` : 'Wiedervorlage'
        
        return [
          { label: 'Alle', key: 'all' },
          { label: 'Bestandskunden', key: 'customers' },
          { label: 'Potentielle Kunden', key: 'leads' },
          { label: 'Interessiert', key: 'interested' },
          { label: 'Nicht Interessiert', key: 'not-interested' },
          { label: callbackTabName, key: 'callbacks' },
          { label:  `Inaktiv (6+ Monate) (${this.stats.inactiveCustomers})`, key: 'inactive' } 
        ]
    },
    filteredCustomers() {
        let filtered = Array.isArray(this.customers) ? [...this.customers] : []

        switch (this.activeTab) {
          case 0: 
            break
          case 1:
            filtered = filtered.filter(c => c.isCustomer === '1')
            break
          case 2:
            filtered = filtered.filter(c => c.isCustomer === '0')
            break
          case 3:
            filtered = filtered.filter(c => c.isInterest === 'Yes')
            break
          case 4:
            filtered = filtered.filter(c => c.isInterest === 'No')
            break
          case 5: 
            filtered = filtered.filter(c => c.callback_date && c.isInterest === 'Later')
            
            filtered.sort((a, b) => {
              try {
                const dateA = new Date(a.callback_date || 0)
                const dateB = new Date(b.callback_date || 0)
                return dateA - dateB
              } catch (error) {
                return 0
              }
            })
            break
          case 6:
            const sixMonthsAgo = new Date()
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6) 
            filtered = filtered.filter(c => {
              // skip if last_pickup is null, empty, or invalid
              if (!c.last_pickup || c.last_pickup.trim() === '' || c.last_pickup === 'N/A') {
                return false
              }

              try {
                const [day, month, year] = c.last_pickup.split('.')

                if (!day || !month || !year || day.length !== 2 || month.length !== 2 || year.length !== 4) {
                  console.warn('Invalid date format:', c.last_pickup)
                  return false
                }
                
                const lastPickupDate = new Date(year, month - 1, day)

                if (isNaN(lastPickupDate.getTime())) {
                  console.warn('Invalid date:', c.last_pickup)
                  return false 
                }
                
                return lastPickupDate < sixMonthsAgo
              } catch (error) {
                console.error('Error parsing date:', c.last_pickup, error)
                return false
              }
            })
            break
        }

      
        if (this.selected_group_main && this.selected_group_main.length > 0) {
          const selectedGroupNames = this.selected_group_main.map(id => 
            this.groups.find(g => g.id === id)?.name
          ).filter(Boolean)
          
          filtered = filtered.filter(customer => 
            selectedGroupNames.includes(customer.group_main_text)
          )
        }
        if (this.selected_group_type && this.selected_group_type.length > 0) {
          filtered = filtered.filter(customer => 
            this.selected_group_type.includes(customer.group_type_text)
          )
        }

        return filtered
    },

    overdueRemindersCount() {
        if (!Array.isArray(this.customers)) return 0
        
        const today = new Date()
        today.setHours(23, 59, 59, 999)
        
        console.log('Today:', today)
        
        const overdueCustomers = this.customers.filter(customer => {
          if (!customer.callback_date || customer.isInterest !== 'Later') return false
          
          try {
            const callbackDate = new Date(customer.callback_date)
            return callbackDate <= today
          } catch (error) {
            console.error('Error parsing date:', customer.callback_date, error)
            return false
          }
        })
        
        console.log('Overdue customers:', overdueCustomers.length)
        console.log('Overdue details:', overdueCustomers.map(c => ({
          company: c.company,
          callback_date: c.callback_date
        })))
        
        return overdueCustomers.length
    },
    conservationStatusOptions() {
          console.log('Converting conservation statuses to options:', this.conservationStatuses)
          
          if (!this.conservationStatuses || this.conservationStatuses.length === 0) {
            console.log('No conservation statuses, using fallback options')
            return this.originalInterestOptions
          }

          const options = this.conservationStatuses.map(status => ({
            text: status.label,
            value: status.keyword,
            color: status.color
          }))
          
          console.log('Converted options:', options)
          return options
    }
  },

  mounted() {
    this.loadCustomers()
    this.calculateStats()
    this.loadGroupMain()
    this.loadGroupTypes()
    this.loadConservationStatuses()
  },

  methods: {

    async handleUpdateCustomer({ customerId, updateData }) {
      console.log('CRMDashboard: Handling customer update:', { customerId, updateData })
      try {
        const param = {
          table: "customer",
          statement: "update",
          where: "true",
          selector: "id",
          value: customerId
        }
        
        console.log('Updating customer with data:', updateData)
        
        await this.$func.queryAPI(param, updateData)

        // Update local state
        Object.assign(this.selectedCustomer, updateData)
        const index = this.customers.findIndex(c => c.id === customerId)
        if (index !== -1) {
          this.customers[index] = { ...this.customers[index], ...updateData }
        }
        
        console.log('Customer updated successfully')
        
        // Refresh stats
        await this.calculateStats()
        
      } catch (error) {
        console.error('Error updating customer:', error)
        alert('Error updating customer information: ' + error.message)
      }
    },
    async handleDeleteConversation(conversation) {
      try {
        const param = {
          table: "conversation",
          statement: "delete",
          where: "true",
          selector: "id",
          value: conversation.id
        }
        
        await this.$func.queryAPI(param, {})
        
        this.conversations = this.conversations.filter(c => c.id !== conversation.id)
        this.$store.dispatch('showSnackbar', {
          message: 'Conversation deleted successfully',
          color: 'success'
        })
        
      } catch (error) {
        console.error('Error deleting conversation:', error)
        this.$store.dispatch('showSnackbar', {
          message: 'Error deleting conversation: ' + error.message,
          color: 'error'
        })
      }
    },

    async loadConservationStatuses() {
      try {
        const param = {
          table: "conservation_status",
          statement: "select",
          type: "list",
          where: "false",
          extension: ""
        }
        
        const result = await this.$func.queryAPI(param, {})
        this.conservationStatuses = Array.isArray(result) ? result : []
        console.log('Loaded conservation statuses:', this.conservationStatuses)
        
      } catch (error) {
        console.error('Error loading conservation statuses:', error)
        this.conservationStatuses = []
      }
    },
  
    getConservationStatusColor(keyword) {
      if (!keyword || keyword === '') {
        console.log('Empty keyword, returning grey')
        return 'grey'
      }
      
      
      if (Array.isArray(this.conservationStatuses)) {
        const status = this.conservationStatuses.find(s => s.keyword === keyword)
        console.log('Found status:', status)
        if (status && status.color) {
          return status.color
        }
      }
      
    
    },

    getConservationStatusLabel(keyword) {
      if (!keyword || keyword === '') {
        return 'Keine'
      }

      if (Array.isArray(this.conservationStatuses)) {
        const status = this.conservationStatuses.find(s => s.keyword === keyword)
        if (status && status.label) {
          return status.label
        }
      }
    },
    async exportData() {
      this.exportLoading = true
      try {
        const dataToExport = this.filteredCustomers.map(customer => {
          
          return {
            'Firma': customer.company || '',
            'Status': customer.isCustomer === '1' ? 'Kunde' : 'Potenziell',
            'PLZ': customer.zipcode || '',
            'Stadt': customer.city || '',
            'Straße': customer.street || '',
            'Telefon': customer.phone || '',
            'Email': customer.email || '',
            'Untergruppe': customer.group_type_text || '',
            'Interesse': this.getConservationStatusLabel(customer.isInterest),
            'Kunde Vorher': this.getCustomerBeforeLabel(customer.customer_before),
            'Letzte Gespräch': customer.lastConversation ? this.formatDate(customer.lastConversation) : '',
            'Wiedervorlage': customer.callback_date ? this.formatDate(customer.callback_date) : '',
            'Rating': customer.rating || '',
            'Rating %': customer.rating_in_percent || '',
            'Letzte Abholung': customer.last_pickup || '',
            'Abholmenge': customer.pickup_qty || '',
            'Bemerkung ': customer.note || '',
            'Created At': customer.created_at ? this.formatDate(customer.created_at) : '',
            'Updated At': customer.updated_at ? this.formatDate(customer.updated_at) : ''
          }
        })


        const headers = Object.keys(dataToExport[0] || {}).join(',')
        const csvContent = dataToExport.map(row => 
          Object.values(row).map(field => 
            `"${String(field || '').replace(/"/g, '""')}"`
          ).join(',')
        ).join('\n')

        const fullCsv = [headers, csvContent].join('\n')

        
        const blob = new Blob([fullCsv], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        link.setAttribute('href', url)
        link.setAttribute('download', `kunden_export_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.$store.dispatch('showSnackbar', {
          message: `Exported ${dataToExport.length} records successfully`,
          color: 'success'
        })

      } catch (error) {
        console.error('Export error:', error)
        this.$store.dispatch('showSnackbar', {
          message: 'Export failed: ' + error.message,
          color: 'error'
        })
      } finally {
        this.exportLoading = false
      }
    },


    openEmailDialog(customer, contact = null) {
      this.selectedCustomer = customer
      this.selectedContact = contact
      this.emailDialog = true
    },
    async handleUpdateCustomer({ customerId, updateData }) {
      console.log('CRMDashboard: Received customer update:', { customerId, updateData })
      try {
        const param = {
          table: "customer",
          statement: "update",
          where: "true",
          selector: "id",
          value: customerId
        }
        
        console.log('Updating customer with data:', updateData)
        
        await this.$func.queryAPI(param, updateData)

      
        if (this.selectedCustomer) {
          this.selectedCustomer = { ...this.selectedCustomer, ...updateData }
        }

        const index = this.customers.findIndex(c => c.id === customerId)
        if (index !== -1) {
         
          this.$set(this.customers, index, { ...this.customers[index], ...updateData })

        }
        
        console.log('Customer updated successfully')
        
        // Refresh stats
        await this.calculateStats()
        
      } catch (error) {
        console.error('Error updating customer:', error)
        alert('Error updating customer information: ' + error.message)
      }
    },  

    async handleFieldUpdate({ field, value }) {
      console.log('CRMDashboard: Received field update:', field, value)
      try {
        const param = {
          table: "customer",
          statement: "update",
          where: "true",
          selector: "id",
          value: this.selectedCustomer.id
        }
        
        const updateData = { [field]: value }
        console.log('Updating customer field with data:', updateData)
        
        await this.$func.queryAPI(param, updateData)
        
        this.$set(this.selectedCustomer, field, value)
        const index = this.customers.findIndex(c => c.id === this.selectedCustomer.id)
        if (index !== -1) {
          this.$set(this.customers[index], field, value)
        }

        await this.calculateStats()      
        console.log('Customer field updated successfully')
        
      } catch (error) {
        console.error('Error updating customer field:', error)
        alert('Error updating customer field: ' + error.message)
      }
    },

    formatNoteForDisplay(note) {
      if (!note) return ''
    
      return note.replace(/\[NEWLINE\]/g, '\n').replace(/\nn/g, '\n')
    },


    async handleConversationSave({ conversationData, customerUpdates, hasCallback, isReset = false }) {
        // console.log('Received conversation data:', conversationData)
        // console.log('Customer updates:', customerUpdates)
        // console.log('Has callback:', hasCallback)
        // console.log('Is reset:', isReset)
        const currentUser = localStorage.getItem('username')
        console.log('Current user:', currentUser)
        
        try {
          const conversationParam = {
            table: "conversation",
            statement: "insert",
            type: "",
            where: "",
            selector: "",
            value: "",
            short: "false",
            extension: ""
          }
          if (conversationData.file_name) {
            conversationData.file_attachment_note = `File attached: ${conversationData.file_name}`
          }

          const result = await this.$func.queryAPI(conversationParam, conversationData)
          console.log('Conversation saved successfully:', result)

          if (customerUpdates && Object.keys(customerUpdates).length > 0) {
            
            customerUpdates.worker = currentUser

            await this.updateCustomerWithCallback(this.selectedCustomer.id, customerUpdates)
          } else {
            await this.updateCustomerLastConversation(this.selectedCustomer.id, new Date().toISOString())
            await this.updateCustomerWorker(this.selectedCustomer.id, currentUser)
          }

          this.conversationDialog = false
          
          
          if (isReset) {
            this.$store.dispatch('showSnackbar', {
            message: 'Callback successfully deleted',
            color: 'success'
            })
          } else if (hasCallback) {
            this.$store.dispatch('showSnackbar', {
            message: 'Conversation saved and callback set',
            color: 'success'
            })
          } else {
            this.$store.dispatch('showSnackbar', {
            message: 'Conversation successfully saved',
            color: 'success'
            })
          }

          await this.handleRefresh()

        } catch (error) {
          console.error('Error saving conversation:', error)
          const errorMessage = isReset ? 
            'Fehler beim Löschen des Rückrufs: ' : 
            'Error saving conversation: '
          alert(errorMessage + (error.message || 'Unknown error'))
        }
    },

    async updateCustomerWorker(customerId, workerName) {
      console.log('Updating worker for customer:', customerId, workerName)
      
      const param = {
        table: "customer",
        statement: "update",
        type: "",
        where: "true",
        selector: "id",
        value: customerId,
        short: "false",
        extension: ""
      }

      const data = {
        worker: workerName,
        lastConversation: new Date().toISOString()
      }

      try {
        await this.$func.queryAPI(param, data)
        console.log('Customer worker updated successfully')
        
        // Update local state
        if (this.selectedCustomer && this.selectedCustomer.id === customerId) {
          this.selectedCustomer.worker = workerName
        }
        
        const index = this.customers.findIndex(c => c.id === customerId)
        if (index !== -1) {
          this.customers[index].worker = workerName
        }
      } catch (error) {
        console.error('Error updating customer worker:', error)
        throw error
      }
    },


    async updateCustomerWithCallback(customerId, customerUpdates) {
      console.log('Updating customer with callback:', customerId, customerUpdates)
      
      const param = {
        table: "customer",
        statement: "update",
        type: "",
        where: "true",
        selector: "id",
        value: customerId,
        short: "false",
        extension: ""
      }

      try {
        await this.$func.queryAPI(param, customerUpdates)
        console.log('Customer updated successfully')
    
        if (this.selectedCustomer && this.selectedCustomer.id === customerId) {
          Object.assign(this.selectedCustomer, customerUpdates)
        }
      } catch (error) {
        console.error('Error updating customer:', error)
        throw error
      }
    },

    async updateCustomerLastConversation(customerId, timestamp) {
      console.log('Updating last conversation for customer:', customerId)
      
      const param = {
        table: "customer",
        statement: "update",
        type: "",
        where: "true",
        selector: "id",
        value: customerId,
        short: "false",
        extension: ""
      }

      const data = {
        lastConversation: timestamp
      }

      try {
        await this.$func.queryAPI(param, data)
        console.log('Customer last conversation updated')
      } catch (error) {
        console.error('Error updating customer last conversation:', error)
      }
    },

    async loadCustomers() {
      const param = {
        table: "customer",
        statement: "select",
        type: "list"
      }
      try {
        const result = await this.$func.queryAPI(param, {})
        this.customers = Array.isArray(result) ? result : []
        await this.calculateStats()
      } catch (error) {
        console.error('Error loading customers:', error)
        this.customers = []
      }
    },

    async calculateStats() {
      if (!Array.isArray(this.customers)) return

      this.stats.totalCustomers = this.customers.filter(c => c.isCustomer === '1').length
      this.stats.totalLeads = this.customers.filter(c => c.isCustomer === '0').length
      this.stats.interestedLeads = this.customers.filter(c => c.isInterest === 'Yes').length

      
      try {
        const userId = localStorage.getItem('user_id')
        const param = {
          table: "conversation",
          statement: "select",
          type: "list",
          where: "true",
          selector: "user_id",
          value: userId
        }
        const allConversations = await this.$func.queryAPI(param, {})
        const inactiveThreshold = new Date()
        inactiveThreshold.setMonth(inactiveThreshold.getMonth() - 6)
        this.stats.inactiveCustomers = this.customers.filter(c => {
          if (!c.last_pickup) return false
          try {
            const [day, month, year] = c.last_pickup.split('.')
            const lastPickupDate = new Date(year, month - 1, day)
            return lastPickupDate < inactiveThreshold
          } catch (error) {
            return false
          }
        }).length
        
        if (Array.isArray(allConversations)) {
          const thirtyDaysAgo = new Date()
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
          
          this.stats.conversationsThisMonth = allConversations.filter(conv => {
            if (!conv.created_at) return false
            return new Date(conv.created_at) >= thirtyDaysAgo
          }).length

            this.stats.offersThisMonth = allConversations.filter(conv => {
            if (!conv.created_at) return false
            const isOffer = conv.conversation_type && 
                          conv.conversation_type.toLowerCase().includes('offer')
            const isRecent = new Date(conv.created_at) >= thirtyDaysAgo
            return isOffer && isRecent
          }).length
         }
      } catch (error) {
        console.error('Error loading conversations for stats:', error)
      }
    },

    async loadGroupMain() {
      const param = {
        table: "group_main",
        statement: "select",
        type: "list"
      }
      try {
        const result = await this.$func.queryAPI(param, {})
        if (Array.isArray(result)) {
          this.groups = result.map(g => ({
            id: Number(g.id),
            name: g.name || "Unknown Group"
          })).sort((a, b) => a.name.localeCompare(b.name))
        }
      } catch (error) {
        console.error('Error fetching groups:', error)
      }
    },
    async loadGroupTypes() {
      const param = {
        table : "group_type",
        statement : "select",
        type : "list"

      }
      try{
        const result = await this.$func.queryAPI(param, {})
        this.groupTypes = Array.isArray(result) ? result : []
        this.groupTypes.sort((a, b) => a.name.localeCompare(b.name))
      } catch(error) {
        console.error('Error fetching group types:', error) 
      }

    },

    async openCustomerDetail(customer) {
      this.selectedCustomer = { ...customer }
      await this.loadConversations(customer.id)
      await this.loadContactPersons(customer.id)
      this.detailDialog = true
    },

    async loadConversations(customerId) {
      const param = {
        table: "conversation",
        statement: "select",
        type: "list",
        where: "true",
        selector: "customer_id",
        value: customerId
      }
      try {
        const result = await this.$func.queryAPI(param, {})
        this.conversations = Array.isArray(result) ? result : []
      } catch (error) {
        console.error('Error loading conversations:', error)
        this.conversations = []
      }
    },

    async loadContactPersons(customerId) {
      const param = {
        table: "contact_person",
        statement: "select",
        type: "list",
        where: "true",
        selector: "customer_id",
        value: customerId
      }
      try {
        const result = await this.$func.queryAPI(param, {})
        this.contactPersons = Array.isArray(result) ? result : []
      } catch (error) {
        console.error('Error loading contact persons:', error)
        this.contactPersons = []
      }
    },

    openConversationDialog(customer) {
      this.selectedCustomer = customer
      this.conversationDialog = true
    },

    openContactPersonDialog(contact = null) {
      this.editingContactPerson = contact
      this.contactPersonDialog = true
    },

    openNewCustomerDialog() {
      this.$router.push('/FormCustomer')
    },

    async sendEmail(emailData) {
      const username = localStorage.getItem('username') 
      const currentTime = new Date().toISOString()

      try {
      
        const emailPayload = {
          to: emailData.to,
          subject: emailData.subject,
          body: emailData.body,
          attachments: emailData.attachments || [],
          customer_id: this.selectedCustomer.id,
          customer_name: this.selectedCustomer.company,
          from_name: username || 'Huska System',
          from_email: 'mariem@inettech.io'
        }

        console.log('Sending email payload:', emailPayload)

      
        const response = await fetch('https://www.migro.net/reifen/crm_email.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(emailPayload)
        })

        const result = await response.json()
        console.log('Email API response:', result)

        if (result.success) {
          
          const param = {
            table: "conversation",
            statement: "insert",
            type: "",
            where: "",
            selector: "",
            value: "",
            short: "false",
            extension: ""
          }

          const conversationData = {
            customer_id: this.selectedCustomer.id,
            conversation_type: 'email',
            note: `Email sent to: ${emailData.to}\nSubject: ${emailData.subject}\n\n${emailData.body}`,
            created_by: username,
            created_at: currentTime,
            updated_at: currentTime,
            file_name: emailData.attachments && emailData.attachments.length > 0 ? 
              `${emailData.attachments.length} attachment(s)` : null
          }

          await this.$func.queryAPI(param, conversationData)
          await this.updateCustomerLastConversation(this.selectedCustomer.id, currentTime)
          
          this.emailDialog = false
          await this.loadConversations(this.selectedCustomer.id)
          await this.loadCustomers()
          
          alert('Email sent successfully! ' + (result.note || ''))
        } else {
          throw new Error(result.message || 'Failed to send email')
        }
        
      } catch (error) {
        console.error('Error sending email:', error)
        alert('Error sending email: ' + (error.message || 'Unknown error'))
      }
    },

    async saveContactPerson(contactData) {
      const isUpdate = !!this.editingContactPerson
      const param = {
        table: "contact_person",
        statement: isUpdate ? "update" : "insert",
        where: isUpdate ? "true" : "",
        selector: isUpdate ? "id" : "",
        value: isUpdate ? this.editingContactPerson.id : ""
      }

      try {
        await this.$func.queryAPI(param, contactData)
        this.contactPersonDialog = false
        await this.loadContactPersons(this.selectedCustomer.id)
      } catch (error) {
        console.error('Error saving contact person:', error)
      }
    },

    async deleteContactPerson() {
      if (!confirm('Delete this contact person?')) return
      
      const param = {
        table: "contact_person",
        statement: "delete",
        where: "true",
        selector: "id",
        value: this.editingContactPerson.id
      }

      try {
        await this.$func.queryAPI(param, {})
        this.contactPersonDialog = false
        await this.loadContactPersons(this.selectedCustomer.id)
      } catch (error) {
        console.error('Error deleting contact person:', error)
      }
    },

    async handleEditNote({ conversation, note }) {
      const param = {
        table: "conversation",
        statement: "update",
        where: "true",
        selector: "id",
        value: conversation.id
      }
      const data = {
        note: note,
        updated_at: new Date().toISOString()
      }

      try {
        await this.$func.queryAPI(param, data)
        await this.loadConversations(this.selectedCustomer.id)
      } catch (error) {
        console.error('Error updating note:', error)
      }
    },

    async handleRefresh() {
      if (this.selectedCustomer) {
        await this.loadConversations(this.selectedCustomer.id)
        await this.loadContactPersons(this.selectedCustomer.id)
      }
      await this.loadCustomers()
    },

    getCustomerBeforeColor(status) {
      const colors = { yes: 'success', no: 'error', unknown: 'grey' }
      return colors[status] || 'grey'
    },

    getCustomerBeforeLabel(status) {
      const labels = { yes: 'Ex-Kunde', no: 'Neukunde', unknown: 'Unbekannt' }
      return labels[status] || 'Unbekannt'
    },

    formatDate(date) {
      if (!date) return '-'
      try {
        const dateObj = new Date(date)
        if (isNaN(dateObj.getTime())) return '-'
        const day = String(dateObj.getDate()).padStart(2, '0')
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const year = dateObj.getFullYear()
        return `${day}.${month}.${year}`
      } catch (error) {
        return '-'
      }
    },
    formatTime(time) {
    if (!time || time === '00:00:00') return 'None'
    try {
    
      const [hours, minutes] = time.split(':')
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
    } catch (error) {
      return time
    }
    },

  }
}
</script>

<style scoped>

</style>